import { useState } from 'react';

import { useMeasure } from '@uidotdev/usehooks';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import { useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';

import { shadeHexColor } from '@/utils';

import { ReeferKpiByModelWithPredicted, store } from '../../store';

export const ReeferKPIContainerContainerHeight = 300;

export const toRoundedMWh = (value: number | null) => {
  return value
    ? value < 1000
      ? (value / 1000).toFixed(1)
      : (value / 1000).toFixed(1)
    : '0.0';
};

// Conversion avec le facteur précis (0,2306306 L/kWh)
const toFormattedVolume = (kwhValue: number | null) => {
  if (!kwhValue) return '0.0';
  const litres = kwhValue * 0.2306306;

  if (litres < 1000) {
    return `${litres.toFixed(1)}`;
  }
  // Convertir en m³ si >= 1000L
  return `${(litres / 1000).toFixed(1)}`;
};

const getVolumeUnit = (kwhValue: number | null) => {
  if (!kwhValue) return 'L';
  const litres = kwhValue * 0.2306306;
  return litres < 1000 ? 'L' : 'm³';
};

// Fonction pour formater l'énergie
const toFormattedEnergy = (kwhValue: number | null) => {
  if (!kwhValue) return '0.0';
  return kwhValue < 1000 ? kwhValue.toFixed(1) : (kwhValue / 1000).toFixed(1);
};

// Fonction pour l'unité d'énergie
const getEnergyUnit = (kwhValue: number | null) => {
  if (!kwhValue) return 'kWh';
  return kwhValue < 1000 ? 'kWh' : 'MWh';
};

const getCo2Unit = (kwhValue: number | null) => {
  if (!kwhValue) return 'kg';
  return kwhValue < 1000 ? 'kg' : 'tons';
};

// Function to format CO2 emissions
const toFormattedCo2 = (co2Emissions: number | null) => {
  if (!co2Emissions) return '0.0';

  if (co2Emissions < 1000) {
    return `${co2Emissions.toFixed(1)}`;
  }
  // Convert to tons if >= 1000 kg
  return `${(co2Emissions / 1000).toFixed(1)}`;
};

function ReeferKPI() {
  const theme = useTheme();
  const snap = useSnapshot(store);
  const reeferKpi = snap.reeferKpi;
  const isLoading = snap.isLoading;
  const { t } = useTranslation();
  const [showEnergy, setShowEnergy] = useState(false);
  // used to adjust centering of element in the chart area (loading, message, ...)

  const [titleRef, { height: titleHeight }] = useMeasure();
  // get our dataset or undefined if not loaded yet
  const dataset: ReeferKpiByModelWithPredicted | null = reeferKpi || {
    total_active_energy_kWh: 0,
    total_co2eq_emissions: 0,
    predicted_co2_emissions: 0,
    reefer_power_on_ratio: 0,
    reefer_usage_stable_ratio: 0,
    reefer_usage_transition_ratio: 0,
    predicted_energy_kWh: 0,
  };

  const {
    total_active_energy_kWh,
    total_co2eq_emissions,
    reefer_power_on_ratio,
    reefer_usage_stable_ratio,
    reefer_usage_transition_ratio,
  } = dataset;

  const allZeros =
    !total_active_energy_kWh &&
    !reefer_power_on_ratio &&
    !reefer_usage_stable_ratio &&
    !reefer_usage_transition_ratio;

  const gaugeProps = {
    width: 65,
    height: 65,
    innerRadius: '70%',
    sx: {
      [`& .${gaugeClasses.valueArc}`]: {
        fill: theme.palette.primary.main,
      },
      [`& .${gaugeClasses.referenceArc}`]: {
        fill: shadeHexColor(theme.palette.primary.main, 0.8),
      },
      [`& .${gaugeClasses.valueText}`]: {
        fontSize: '0.75rem',
      },
    },
  };

  const getPerformanceColor = (actual: number, predicted: number) => {
    if (!actual || !predicted) return theme.palette.grey[400];
    const ratio = actual / predicted;
    if (ratio <= 1.5) return theme.palette.success.main; // Normal : <= 150%
    if (ratio <= 2.0) return theme.palette.warning.main; // Attention : entre 150% et 200%
    return theme.palette.error.main; // Critique : > 200%
  };

  const performanceColor = getPerformanceColor(
    total_active_energy_kWh || 0,
    dataset?.predicted_energy_kWh || 0,
  );

  const getPerformanceStatus = (actual: number, predicted: number) => {
    if (!actual || !predicted) return '';
    const ratio = actual / predicted;
    if (ratio <= 1.5) return t('reeferInsights.kpi.normalPerformance');
    if (ratio <= 2.0) return t('reeferInsights.kpi.poorPerformance');
    return t('reeferInsights.kpi.criticalPerformance');
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '20px',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'auto',
        position: 'relative',
      }}
    >
      <Box
        ref={titleRef}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          pl: 3,
          pt: 2,
          gap: 1,
          flexGrow: 1,
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        {/* {isLoading && <CircularProgress />} */}

        {/* {!isLoading && allZeros && (
          <Typography variant="overline" sx={{ mt: `-${titleHeight}px` }}>
            {t('noData')}
          </Typography>
        )} */}

        {
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 1,
              }}
            >
              <Typography variant="body2">
                {showEnergy ? 'kWh' : 'Co2'}
              </Typography>
              <Switch
                checked={showEnergy}
                onChange={(e) => setShowEnergy(e.target.checked)}
                color="primary"
                size="small"
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 4,
              }}
            >
              {/* Colonne des totaux */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'flex-end',
                }}
              >
                {/* Total actuel */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t(
                      `reeferInsights.kpi.totalActual${showEnergy ? 'Energy' : 'Co2e'}`,
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{ color: performanceColor }}
                    >
                      {showEnergy
                        ? toFormattedEnergy(total_active_energy_kWh)
                        : toFormattedCo2(total_co2eq_emissions)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: performanceColor }}
                    >
                      {showEnergy
                        ? getEnergyUnit(total_active_energy_kWh)
                        : getCo2Unit(total_co2eq_emissions)}
                    </Typography>
                  </Box>
                </Box>

                {/* Total prédit */}
                <Tooltip
                  title={t('reeferInsights.kpi.predictionExplanation')}
                  arrow
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'help',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t(
                        `reeferInsights.kpi.totalPredicted${showEnergy ? 'Energy' : 'Co2e'}`,
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">
                        {showEnergy
                          ? toFormattedEnergy(
                              dataset?.predicted_energy_kWh || 0,
                            )
                          : toFormattedCo2(
                              dataset?.predicted_co2_emissions || 0,
                            )}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {showEnergy
                          ? getEnergyUnit(dataset?.predicted_energy_kWh || 0)
                          : getCo2Unit(dataset?.predicted_co2_emissions || 0)}
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
              </Box>

              {/* Indicateur de performance */}
              <Tooltip
                title={t('reeferInsights.kpi.performanceExplanation')}
                arrow
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    cursor: 'help',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: performanceColor,
                      fontWeight: 'medium',
                    }}
                  >
                    {getPerformanceStatus(
                      total_active_energy_kWh || 0,
                      dataset?.predicted_energy_kWh || 0,
                    )}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        backgroundColor: theme.palette.error.main,
                        borderRadius: '50%',
                        opacity:
                          performanceColor === theme.palette.error.main
                            ? 1
                            : 0.3,
                      }}
                    />
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        backgroundColor: theme.palette.warning.main,
                        borderRadius: '50%',
                        opacity:
                          performanceColor === theme.palette.warning.main
                            ? 1
                            : 0.3,
                      }}
                    />
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        backgroundColor: theme.palette.success.main,
                        borderRadius: '50%',
                        opacity:
                          performanceColor === theme.palette.success.main
                            ? 1
                            : 0.3,
                      }}
                    />
                  </Box>
                </Box>
              </Tooltip>
            </Box>

            <Divider sx={{ width: '90%', alignSelf: 'center' }} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="overline">
                  {t('operatingPhase.powerOn')}
                </Typography>
                <Gauge
                  value={
                    reefer_power_on_ratio
                      ? Math.round(reefer_power_on_ratio)
                      : 0
                  }
                  text={({ value }) => `${value} %`}
                  {...gaugeProps}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ visibility: 'hidden' }}>
                    -
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="overline">
                  {t('operatingPhase.stable')}
                </Typography>
                <Gauge
                  value={
                    reefer_usage_stable_ratio
                      ? Math.round(reefer_usage_stable_ratio)
                      : 0
                  }
                  text={({ value }) => `${value} %`}
                  {...gaugeProps}
                />
                <Typography variant="subtitle1">
                  {showEnergy
                    ? toFormattedEnergy(
                        ((reefer_usage_stable_ratio || 0) / 100) *
                          (dataset?.total_active_energy_kWh || 0),
                      )
                    : toFormattedCo2(
                        ((reefer_usage_stable_ratio || 0) / 100) *
                          (total_co2eq_emissions || 0),
                      )}
                  {showEnergy
                    ? getEnergyUnit(
                        ((reefer_usage_stable_ratio || 0) / 100) *
                          (total_active_energy_kWh || 0),
                      )
                    : getCo2Unit(
                        ((reefer_usage_stable_ratio || 0) / 100) *
                          (total_co2eq_emissions || 0),
                      )}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="overline">
                  {t('operatingPhase.transition')}
                </Typography>
                <Gauge
                  value={
                    reefer_usage_transition_ratio
                      ? Math.round(reefer_usage_transition_ratio)
                      : 0
                  }
                  text={({ value }) => `${value} %`}
                  {...gaugeProps}
                />
                <Typography variant="subtitle1">
                  {showEnergy
                    ? toFormattedEnergy(
                        ((reefer_usage_transition_ratio || 0) / 100) *
                          (dataset.total_active_energy_kWh || 0),
                      )
                    : toFormattedCo2(
                        ((reefer_usage_transition_ratio || 0) / 100) *
                          (total_co2eq_emissions || 0),
                      )}
                  {showEnergy
                    ? getEnergyUnit(
                        ((reefer_usage_transition_ratio || 0) / 100) *
                          (total_active_energy_kWh || 0),
                      )
                    : getCo2Unit(
                        ((reefer_usage_transition_ratio || 0) / 100) *
                          (total_co2eq_emissions || 0),
                      )}
                </Typography>
              </Box>
            </Box>
          </Box>
        }
      </Box>
    </Box>
  );
}

export { ReeferKPI };
